/* eslint-disable max-lines */
import { computed, nextTick, onBeforeUnmount, ref, toValue, watch } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import { type RouteLocationPathRaw, onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { JobApplicantStatus } from '@factoryfixinc/ats-interfaces';
import type { ProjectWithRelations } from '@factoryfixinc/ats-interfaces';

import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import { CANDIDATE_TABS, getCurrentCandidateTab } from '@/composables/jobs/candidateTabs';
import type { CandidateTab } from '@/composables/jobs/candidateTabs';
import {
  FF_HINT_CONVERSATION_ID,
  FF_HINT_STATUS,
} from '@/composables/jobs/useConversationStatusNavigation';
import type {
  ConversationIndex,
  SearchConversationIndex,
} from '@/core/conversations/conversation-index/conversation-index.type';
import {
  originsForDeepLinkedConversation,
  withPrimaryApplicationForJob,
} from '@/core/conversations/conversation-index/deep-linked-conversation.util';
import {
  confirmPendingGlobalSearchJump,
  discardPendingGlobalSearchJump,
  failPendingGlobalSearchJump,
} from '@/core/conversations/conversation-index/global-search-jump';
import type { PendingGlobalSearchJump } from '@/core/conversations/conversation-index/global-search-jump';
import { ErrorService } from '@/core/shared/errors/error.service';
import { ProjectNotFoundError } from '@/core/shared/errors/project-not-found.error';
import ProjectService from '@/core/shared/project/project.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import MeService from '@/core/shared/me/me.service';

/**
 * Intelligently selects the default status based on candidate counts.
 * Priority order: CLIENT (Engaged) -> NEW -> CONTACT (Outreach)
 * @param project The project containing candidate counts
 * @returns The best default status based on available candidates
 */
function getSmartDefaultStatus(project: ProjectWithRelations<'candidates'>): JobApplicantStatus {
  // Priority order: CLIENT (Engaged) -> NEW -> CONTACT (Outreach)
  const statusPriority = [
    JobApplicantStatus.CLIENT, // Try Engaged first
    JobApplicantStatus.NEW, // Then New
    JobApplicantStatus.CONTACT, // Then Outreach
  ];

  // If the project has candidate counts, find the first status with candidates
  if (project.candidates) {
    for (const status of statusPriority) {
      // Type-safe access to candidate counts with proper type checking
      const candidateCount = (project.candidates as Record<string, number | undefined>)[status];
      if (candidateCount && candidateCount > 0) {
        return status;
      }
    }
  }

  // Default to CONTACT if no candidates found or no counts available
  return JobApplicantStatus.CONTACT;
}

const KNOWN_STATUSES: string[] = Object.values(JobApplicantStatus);

/** The valid statuses in an `applicationStatus` query value, which may be comma-separated. */
function getStatusesFromQuery(value: unknown): JobApplicantStatus[] {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== 'string') {
    return [];
  }
  return raw.split(',').filter((status) => KNOWN_STATUSES.includes(status)) as JobApplicantStatus[];
}

/**
 * Merges the live filter state (the user's current toggles, sort and origins) with explicit
 * search parameters. Never reads the page-load localStorage snapshot, which goes stale as soon
 * as the user changes or clears a filter.
 */
function withLiveFilters(
  conversationIndexService: ConversationIndexService,
  params: Partial<SearchConversationIndex>,
): Partial<SearchConversationIndex> {
  const { onlyCurrentUser, onlyUnread, orderBy, origins } =
    conversationIndexService.conversationIndexSearch;

  return { onlyCurrentUser, onlyUnread, orderBy, origins, ...params };
}

/**
 * Handles routes for a specific conversation, like `/jobs/:projectId/candidates/:conversationId`.
 * This function is optimized to fetch only the required conversation without changing
 * the user's selected status filters or reloading the main list unnecessarily.
 * @param targetProjectId The ID of the project.
 * @param targetConversationId The ID of the conversation to load.
 * @param isUserClickNavigation If true, user clicked a card - only select without list manipulation.
 * @param existingConversation Optional pre-fetched conversation to avoid duplicate requests.
 */
export async function handleConversationRoute(
  targetProjectId: number,
  targetConversationId: number,
  isUserClickNavigation = false,
  existingConversation?: ConversationIndex | null,
  signal?: AbortSignal,
): Promise<void> {
  const projectService = new ProjectService();
  const conversationIndexService = new ConversationIndexService();

  if (projectService.currentProject?.id !== targetProjectId) {
    // getProjectById throws ProjectNotFoundError on 404, which the main route
    // watcher catches and turns into a graceful redirect to /jobs.
    await projectService.getProjectById(targetProjectId, { signal });
    if (signal?.aborted) return;
  }

  // If this is a user click navigation and the target is already in the current list,
  // just select it — no need to disturb the filter or refetch. When the target is NOT in
  // the list (e.g., the user clicked `View there` in the post-status-change snackbar to
  // jump to a candidate whose new status doesn't match the active filter), fall through
  // to the full flow so the filter updates to the target's status and the list refreshes
  // to match. `filterLocalConversationIndexesByStatus` keeps the local list accurate by
  // removing moved candidates whose new status no longer matches the filter, so this
  // `in list` check is a reliable signal.
  if (isUserClickNavigation) {
    const existingInList = conversationIndexService.conversationIndexes.find(
      (c) => c.conversationId === targetConversationId,
    );

    if (existingInList) {
      await conversationIndexService.selectConversationIndex(existingInList, signal);
      return;
    }
  }

  // Original behavior for non-user-click navigation
  // Get the currentJobId first to avoid unnecessary requests
  const currentJobId = projectService.currentProject?.jobId;
  if (currentJobId === undefined) {
    throw new Error('Current project does not have a job ID');
  }

  // Use existing conversation if provided, otherwise fetch it
  const targetConversation =
    existingConversation ||
    (await conversationIndexService.fetchConversationByConversationId(targetConversationId));
  if (signal?.aborted) return;
  if (!targetConversation) {
    throw new Error('Conversation not found');
  }

  // Find the application for the current job, not just the primary one
  const applicationForCurrentJob = targetConversation.applicationIndexes?.find(
    (app) => app.jobId === currentJobId,
  );

  // The conversation index is eventually-consistent — prefer the hinted status from
  // the snackbar's View there action over the (possibly stale) fetched status.
  const hintedStatus =
    window.history.state?.[FF_HINT_CONVERSATION_ID] === targetConversationId
      ? (window.history.state[FF_HINT_STATUS] as string)
      : undefined;

  const status =
    hintedStatus ||
    applicationForCurrentJob?.applicationStatus ||
    targetConversation.primaryApplicationIndex?.applicationStatus;

  if (!status) {
    throw new Error('Conversation has no status for this job');
  }

  // Update the filters and load conversations with the correct jobId and status in one operation
  conversationIndexService.selectedStatusList = [status];
  conversationIndexService.selectedStatusFilters = [status];

  // Set the jobIds in the search to ensure all requests have the correct jobId
  conversationIndexService.conversationIndexSearch.jobIds = [currentJobId];

  // Status and jobIds take precedence over the live filters. Origins is widened to all
  // origins when the deep-linked candidate falls outside the active selection.
  const searchParams = withLiveFilters(conversationIndexService, {
    applicationStatus: [status],
    jobIds: [currentJobId],
    origins: originsForDeepLinkedConversation(
      conversationIndexService.conversationIndexSearch.origins,
      applicationForCurrentJob || targetConversation.primaryApplicationIndex,
    ),
  });

  // Refresh the project to get updated counts for the status chips
  await projectService.getProjectById(targetProjectId, { select: false });
  if (signal?.aborted) return;

  // Force a refresh to ensure we have the correct conversations for the given status.
  await conversationIndexService.updateConversationIndexSearch(searchParams, {
    resetConversationIndexes: true,
    resetPagination: true,
    doNotSelectFirst: true, // Prevent the service from auto-selecting the first item.
    signal,
  });
  if (signal?.aborted) return;

  // Ensure the deep-linked target is at the top of the list. If it's missing from the
  // page-1 results we just fetched, insert it — the filter above was set to the target's
  // own status and widened to the target's own origin, so the inserted candidate matches
  // both the active status and origin filters (no ghost rows under the wrong status, which
  // was the original ONE-5842 symptom). All three deep-link entry points do this; only
  // onlyUnread/onlyCurrentUser mismatches still pin a row the active filter excludes. The
  // "advance route after status change" behavior added in this PR keeps the URL from ever
  // pointing at a status-mismatched candidate within a session, so this insert path now
  // only fires for genuine deep-linked / cross-session navigation.
  const existingIndex = conversationIndexService.conversationIndexes.findIndex(
    (c) => c.conversationId === targetConversationId,
  );

  // Use the fetched conversation data (which has all enriched data) when adding to list
  if (existingIndex === -1) {
    // If it's not in the list at all (e.g., on a different page), add it to the top.
    conversationIndexService.conversationIndexes.unshift(
      withPrimaryApplicationForJob(targetConversation, currentJobId),
    );
  } else if (existingIndex > 0) {
    // If it exists but is not at the top, move it.
    const [conversationToMove] = conversationIndexService.conversationIndexes.splice(
      existingIndex,
      1,
    );
    conversationIndexService.conversationIndexes.unshift(conversationToMove);
  }

  // Select the freshly-fetched target. Passing it directly (rather than reading
  // `conversationIndexes[0]`) is robust if any future change skips the insert above.
  await conversationIndexService.selectConversationIndex(targetConversation, signal);
}

/**
 * Load saved filter preferences from localStorage
 */
function loadSavedFilters(meService: MeService): Partial<SearchConversationIndex> | null {
  try {
    const currentUserAndEmployerId = `${meService.userProfile?.id}-${meService.employer?.id}`;
    const queryLocalStorage = localStorage.getItem(`filter-query-${currentUserAndEmployerId}`);
    return queryLocalStorage ? JSON.parse(queryLocalStorage) : null;
  } catch (error) {
    ErrorService.captureException(error);
    return null;
  }
}

/**
 * A composable to manage the complex routing, data fetching, and state
 * management for the main jobs page (`/jobs`).
 *
 * @param options.disableAutoRedirect When truthy, suppresses the auto-redirect from
 *   `/jobs` (and `/jobs/:projectId/candidates`) to the first conversation. The mobile
 *   experience drives this with the phone gate so phones land on the Activity inbox
 *   instead of being bounced into a conversation. Desktop leaves it unset.
 */
export function useJobsRouting(
  options: { disableAutoRedirect?: MaybeRefOrGetter<boolean> } = {},
): void {
  const route = useRoute();
  const router = useRouter();
  const projectService = new ProjectService();
  const conversationIndexService = new ConversationIndexService();
  const meService = new MeService();

  const isAutoRedirectDisabled = (): boolean => toValue(options.disableAutoRedirect) === true;

  // Composable-scoped state for abort management
  // Using a ref ensures proper reactivity and cleanup
  const abortState = ref<{
    controller: AbortController | null;
    recentlyAborted: boolean;
  }>({
    controller: null,
    recentlyAborted: false,
  });

  // Load saved filters once on initialization
  const savedFilters = loadSavedFilters(meService);

  // Helper to check if we're on a managed route
  function isOnManagedRoute(): boolean {
    return route.path.startsWith('/jobs');
  }

  // Create a new abort controller and cancel the previous one
  function createNewAbortController(): AbortController {
    // Cancel any existing operations
    if (abortState.value.controller) {
      abortState.value.controller.abort();
      abortState.value.recentlyAborted = true;
      // Clear flag after a short delay
      setTimeout(() => {
        abortState.value.recentlyAborted = false;
      }, 100);
    }
    // Create new controller
    abortState.value.controller = new AbortController();
    return abortState.value.controller;
  }

  // Safe navigation wrapper that only navigates if not aborted
  async function safeJobsNavigate(
    location: RouteLocationPathRaw,
    method: 'push' | 'replace' = 'push',
    signal?: AbortSignal,
  ): Promise<void> {
    // Check both the passed signal and the abort controller state
    if (signal?.aborted || abortState.value.controller?.signal.aborted || !isOnManagedRoute()) {
      return;
    }

    programmaticNavigationTarget = location.path;
    try {
      await router[method](location);
    } finally {
      // The route watcher may skip the navigation during the recent-abort window.
      if (programmaticNavigationTarget === location.path) {
        programmaticNavigationTarget = null;
      }
    }
  }

  // Recovery redirect to `/jobs` after a route fails to resolve. Deliberately not routed
  // through `safeJobsNavigate`: that marks the navigation as programmatic, which makes the
  // watcher skip it, so nothing selects a project and the page is left showing the previous
  // job's list with no current project.
  async function recoverToJobsIndex(signal?: AbortSignal): Promise<void> {
    if (signal?.aborted || abortState.value.controller?.signal.aborted || !isOnManagedRoute()) {
      return;
    }

    await router.replace({ path: '/jobs' });
  }

  // A candidate click started after the list landed owns the selection, so the list's
  // auto-select must not redirect over it (or cancel its still-pending navigation).
  async function redirectToAutoSelected(
    location: RouteLocationPathRaw,
    signal: AbortSignal,
  ): Promise<void> {
    if (conversationIndexService.hasPendingCardSelection) return;
    await safeJobsNavigate(location, 'push', signal);
  }

  // Apply saved filters to the conversation search immediately if they exist
  if (savedFilters) {
    // Apply orderBy and other filter preferences
    if (savedFilters.orderBy) {
      conversationIndexService.conversationIndexSearch.orderBy = savedFilters.orderBy;
    }
    if (savedFilters.onlyCurrentUser !== undefined) {
      conversationIndexService.conversationIndexSearch.onlyCurrentUser =
        savedFilters.onlyCurrentUser;
    }
    if (savedFilters.onlyUnread !== undefined) {
      conversationIndexService.conversationIndexSearch.onlyUnread = savedFilters.onlyUnread;
    }
    if (savedFilters.origins !== undefined) {
      conversationIndexService.conversationIndexSearch.origins = savedFilters.origins;
    }
  }

  // =================================================================================
  // Computed properties to reactively parse information from the URL
  // =================================================================================

  const projectId = computed(() => {
    const id = route.params.projectId;
    if (!id) return undefined;
    const numId = Number(id);
    return isNaN(numId) ? undefined : numId;
  });

  const conversationId = computed(() => {
    const id = route.params.conversationId;
    if (!id) return undefined;
    const numId = Number(id);
    return isNaN(numId) ? undefined : numId;
  });

  const jobApplicantId = computed(() => {
    const id = route.query.jobApplicantId;
    if (!id) return undefined;
    const numId = Number(id);
    return isNaN(numId) ? undefined : numId;
  });

  const queryConversationId = computed(() => {
    const id = route.query.conversationId;
    if (!id) return undefined;
    const numId = Number(id);
    return isNaN(numId) ? undefined : numId;
  });

  const jobId = computed(() => {
    const id = route.query.jobId;
    if (!id) return undefined;
    const numId = Number(id);
    return isNaN(numId) ? undefined : numId;
  });

  // =================================================================================
  // Route Handlers
  // =================================================================================

  // A filter change during the first search discards that search's list, so the landing
  // must wait for the list the change asked for instead of reading an empty one.
  function waitForProjectListSearch(signal: AbortSignal): Promise<void> {
    if (!projectService.isSearchingProjects) return Promise.resolve();
    return new Promise((resolve) => {
      const stop = watch(
        () => projectService.isSearchingProjects,
        (isSearching) => {
          if (isSearching) return;
          stop();
          signal.removeEventListener('abort', onAbort);
          resolve();
        },
      );
      function onAbort() {
        stop();
        resolve();
      }
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  /**
   * Handles the `/jobs` route where no project is selected.
   * It finds the first available project and navigates to it, using a smart
   * default status based on available candidates.
   */
  async function handleNoProjectRoute(signal: AbortSignal) {
    if (signal.aborted) {
      return;
    }

    // The service resolves the effective filter and owns the first search, so this
    // landing can no longer issue an unfiltered one that outlives the filtered one.
    // It always settles, even on a failed bootstrap, so this await cannot deadlock.
    await projectService.ensureInitialSearch({
      query: route.query,
      onFilterResolved: (resolvedQuery) => {
        // The search carries no signal, so a slow first load can resolve after the
        // user has left; a same-path replace would rewrite the new page's query.
        if (!isOnManagedRoute()) return;
        return router.replace({ query: { ...route.query, ...resolvedQuery } });
      },
    });

    if (signal.aborted) {
      return;
    }

    if (!projectService.projects[0]) {
      await waitForProjectListSearch(signal);
      if (signal.aborted) return;
    }

    const firstProject = projectService.projects[0];
    if (!firstProject) {
      // No projects exist for this employer, stay on the page to show empty state.
      return;
    }

    // Select the project before loading the list. Every other handler resolves the project
    // first; doing it last here means an abort in between leaves the list scoped to a job
    // with no current project, and anything reading `currentProject.jobId` (bulk export)
    // then sees no job id at all.
    projectService.selectFirstProject();

    // Smart default based on candidate counts
    const defaultStatus = getSmartDefaultStatus(firstProject);
    conversationIndexService.selectedStatusList = [defaultStatus];
    conversationIndexService.selectedStatusFilters = [defaultStatus];

    const searchParams = withLiveFilters(conversationIndexService, {
      applicationStatus: [defaultStatus],
      jobIds: [firstProject.jobId],
    });

    await conversationIndexService.updateConversationIndexSearch(searchParams, {
      resetConversationIndexes: true,
      resetPagination: true,
      signal,
    });

    // Final abort check before navigation
    if (signal.aborted) {
      return;
    }

    // The mobile Activity inbox owns this landing: skip the auto-redirect so the phone
    // stays on `/jobs` and renders the inbox. The list above is still loaded so toggling
    // to the full version has data ready.
    if (isAutoRedirectDisabled()) {
      return;
    }

    // Navigate to the first conversation found, preserving the user's current tab.
    // Using a hardcoded `job-conversation-tab` here was the original ONE-5844
    // symptom: switching projects from `/.../resume` silently bounced the user
    // to `/conversation`.
    const firstConversation = conversationIndexService.conversationIndexes[0];
    // The filter write-back aborted a controller during this landing, and the
    // latch would make the watcher swallow the landing's own redirect.
    abortState.value.recentlyAborted = false;
    if (firstConversation) {
      const currentTab = getCurrentCandidateTab(route.path);
      await redirectToAutoSelected(
        {
          path: `/jobs/${firstProject.id}/candidates/${firstConversation.conversationId}/${currentTab}`,
          // The jobs scope the resolver just wrote is part of this URL's meaning;
          // a path-only navigation would drop it before the landing finishes.
          query: route.query,
        },
        signal,
      );
    } else {
      // If no conversations are found, navigate to the project's candidate page,
      // which will show an empty state.
      await safeJobsNavigate(
        {
          path: `/jobs/${firstProject.id}/candidates`,
          query: route.query,
        },
        'push',
        signal,
      );
    }
  }

  /**
   * Handles routes for a specific project, like `/jobs/:projectId/candidates`.
   * @param targetProjectId The ID of the project to load.
   * @param preserveStatus If true, keeps the existing status filter; otherwise, uses smart default.
   */
  async function handleProjectRoute(
    targetProjectId: number,
    preserveStatus = false,
    signal: AbortSignal,
  ) {
    if (projectService.currentProject?.id !== targetProjectId) {
      // getProjectById throws ProjectNotFoundError on 404, caught below
      // to redirect to /jobs instead of surfacing a generic critical error.
      await projectService.getProjectById(targetProjectId, { signal });

      if (signal.aborted) {
        return;
      }
    }

    // A status in the URL is the stage this route was left on (see the empty-result cleanup
    // below). It outranks the in-memory selection, which a reload has lost and a visit to
    // another job may have changed.
    const statusesFromUrl = getStatusesFromQuery(route.query.applicationStatus);
    if (statusesFromUrl.length > 0) {
      conversationIndexService.selectedStatusList = statusesFromUrl;
      conversationIndexService.selectedStatusFilters = statusesFromUrl;
    } else if (!preserveStatus || conversationIndexService.selectedStatusList.length === 0) {
      // Only set default status if we're not preserving and have no status selected
      // currentProject is guaranteed to exist here due to the check above
      const currentProject = projectService.currentProject;
      if (currentProject) {
        const defaultStatus = getSmartDefaultStatus(currentProject);
        conversationIndexService.selectedStatusList = [defaultStatus];
        conversationIndexService.selectedStatusFilters = [defaultStatus];
      }
    }

    const searchParams = withLiveFilters(conversationIndexService, {
      applicationStatus: conversationIndexService.selectedStatusList,
      jobIds: [projectService.currentProject?.jobId as number],
    });

    await conversationIndexService.updateConversationIndexSearch(searchParams, {
      resetConversationIndexes: true,
      resetPagination: true,
      signal,
    });
    if (signal.aborted) {
      return;
    }

    // On mobile this route is the Activity inbox (back out of a conversation drops the
    // conversationId to `/jobs/:projectId/candidates`). Skip the auto-redirect so the
    // back button returns to the inbox instead of re-opening the first conversation.
    if (isAutoRedirectDisabled()) {
      return;
    }

    // Auto-select the first conversation, preserving the user's current tab.
    // ONE-5844: hardcoding `job-conversation-tab` here flipped the URL to
    // /conversation when the user was on /resume.
    const firstConversation = conversationIndexService.conversationIndexes[0];
    if (firstConversation) {
      const currentTab = getCurrentCandidateTab(route.path);
      // Same as the landing above: this redirect must not be eaten by a latch
      // this handler's own aborts armed.
      abortState.value.recentlyAborted = false;
      await redirectToAutoSelected(
        {
          path: `/jobs/${targetProjectId}/candidates/${firstConversation.conversationId}/${currentTab}`,
          query: route.query,
        },
        signal,
      );
    }
    // If no conversations, remain on the project page to show the empty state.
  }

  /**
   * Handles navigation from a query parameter, resolving the `jobApplicantId` to a
   * specific conversation and navigating to it. This is the third deep-link entry point
   * (email links and global-search jumps); `fetchConversationIndexesByApplicantIdWithFilters`
   * gives it the same status-and-origin guarantee the other two give here.
   * @param targetJobApplicantId The ID of the job applicant.
   * @param targetJobId The ID of the associated job (optional).
   * @returns true when the landing fully resolved, false when it was aborted
   *   mid-flight. The result is decided *before* the URL-cleaning replace below,
   *   because that replace re-triggers the route watcher whose next run aborts
   *   this run's controller — checking `signal.aborted` after this function
   *   resolves would misread every successful landing as aborted (ONE-6912).
   */
  async function handleJobApplicantRoute(
    targetJobApplicantId: number,
    targetJobId: number | undefined,
    signal: AbortSignal,
  ): Promise<boolean> {
    let project;

    // Fetch the applicant conversation once to determine the project
    const applicantConversation =
      await conversationIndexService.fetchConversationByApplicantId(targetJobApplicantId);

    if (signal.aborted) return false;

    if (!applicantConversation) {
      throw new Error('Candidate not found');
    }

    if (targetJobId) {
      project = await projectService.getProjectByJobIdFromDeepLink(targetJobId, { signal });
    } else {
      // If no job ID, use the primary job ID from the conversation
      const primaryJobId = applicantConversation.primaryApplicationIndex?.jobId;
      if (!primaryJobId) {
        throw new Error(`Could not find a project for job applicant ${targetJobApplicantId}`);
      }
      project = await projectService.getProjectByJobIdFromDeepLink(primaryJobId, { signal });
    }

    if (signal.aborted) return false;

    if (!project?.id) {
      throw new Error(`Project not found for job applicant ${targetJobApplicantId}`);
    }

    // Set the current project if it's different
    if (projectService.currentProject?.id !== project.id) {
      await projectService.getProjectById(project.id, { signal });
      if (signal.aborted) return false;
    }

    // Use the specialized method that fetches by applicant ID and sets up filters. It
    // owns the status *and* origin widening for this path, because the target application
    // row is only known once it has scoped the conversation to that one application.
    await conversationIndexService.fetchConversationIndexesByApplicantIdWithFilters(
      targetJobApplicantId,
      signal,
    );

    if (signal.aborted) return false;

    // Determine the target tab from query param or current route. Validate against
    // the known tabs (mirroring handleConversationQueryParam) so a junk `tab` query
    // can't build a path the router resolves to the wrong pane.
    const tabFromQuery = CANDIDATE_TABS.includes(route.query.tab as CandidateTab)
      ? (route.query.tab as CandidateTab)
      : undefined;
    const targetTab = tabFromQuery || getCurrentCandidateTab(route.path);

    // Clean up the URL by removing the query parameters, as they have been resolved.
    await safeJobsNavigate(
      {
        path: `/jobs/${project.id}/candidates/${applicantConversation.conversationId}/${targetTab}`,
        query: {
          ...route.query,
          jobApplicantId: undefined,
          jobId: undefined,
          tab: undefined,
        },
      },
      'replace',
      signal,
    );

    // The landing (project, filters, list, selection) resolved — the cleanup
    // above is cosmetic even when safeJobsNavigate skipped it.
    return true;
  }

  /**
   * Handles navigation from a `conversationId` in the query parameters.
   * @param targetConversationId The ID of the conversation.
   * @param targetJobId An optional job ID to help find the project faster.
   */
  async function handleConversationQueryParam(
    targetConversationId: number,
    targetJobId: number | undefined,
    signal: AbortSignal,
  ) {
    let project;
    let conversation: ConversationIndex | null = null;

    // Fetch the conversation first to get full context
    conversation =
      await conversationIndexService.fetchConversationByConversationId(targetConversationId);

    if (signal.aborted) return;

    if (!conversation) {
      throw new Error(`Conversation ${targetConversationId} not found`);
    }

    if (targetJobId) {
      project = await projectService.getProjectByJobIdFromDeepLink(targetJobId, { signal });
    } else {
      // Find the first valid jobId from the conversation's applications
      const validJobId = conversation.applicationIndexes?.find((app) => app.jobId)?.jobId;
      if (!validJobId) {
        throw new Error(`Could not find a valid job for conversation ${targetConversationId}`);
      }
      project = await projectService.getProjectByJobIdFromDeepLink(validJobId, { signal });
    }

    if (signal.aborted) return;

    if (!project?.id) {
      throw new Error(`Project not found for conversation ${targetConversationId}`);
    }

    // Set the current project first (always refresh to get updated counts)
    await projectService.getProjectById(project.id, { signal });

    if (signal.aborted) return;

    // Find the status for this specific job
    const currentJobId = project.jobId;
    const applicationForJob = conversation.applicationIndexes?.find(
      (app) => app.jobId === currentJobId,
    );

    const status =
      applicationForJob?.applicationStatus ||
      (projectService.currentProject
        ? getSmartDefaultStatus(projectService.currentProject)
        : JobApplicantStatus.CONTACT);

    // Set up the filters correctly
    conversationIndexService.selectedStatusList = [status];
    conversationIndexService.selectedStatusFilters = [status];
    conversationIndexService.conversationIndexSearch.jobIds = [currentJobId];

    // Load conversations with the correct filters
    const searchParams = withLiveFilters(conversationIndexService, {
      applicationStatus: [status],
      jobIds: [currentJobId],
      origins: originsForDeepLinkedConversation(
        conversationIndexService.conversationIndexSearch.origins,
        applicationForJob || conversation.primaryApplicationIndex,
      ),
    });

    await conversationIndexService.updateConversationIndexSearch(searchParams, {
      resetConversationIndexes: true,
      resetPagination: true,
      doNotSelectFirst: true,
      signal,
    });

    if (signal.aborted) return;

    // Ensure the deep-linked target is at the top of the list. See the matching comment
    // in handleConversationRoute for why force-inserting is safe here: the filter was just
    // set to the target's status and widened to the target's origin, so the inserted
    // candidate matches both the active status and origin filters (no ghost rows under the
    // wrong status). Only onlyUnread/onlyCurrentUser mismatches still pin the row at the
    // top. This path mostly fires for deep links via `?conversationId=`.
    const existingIndex = conversationIndexService.conversationIndexes.findIndex(
      (c) => c.conversationId === targetConversationId,
    );

    if (existingIndex === -1) {
      // Add it to the top if not in list
      conversationIndexService.conversationIndexes.unshift(
        withPrimaryApplicationForJob(conversation, currentJobId),
      );
    } else if (existingIndex > 0) {
      // Move to top if not already there
      const [conversationToMove] = conversationIndexService.conversationIndexes.splice(
        existingIndex,
        1,
      );
      conversationIndexService.conversationIndexes.unshift(conversationToMove);
    }

    // Select the conversation
    await conversationIndexService.selectConversationIndex(conversation, signal);

    if (signal.aborted) return;

    // Prefer an explicit `tab` query over the current path's tab, mirroring
    // `handleJobApplicantRoute`. The mobile Activity inbox passes `tab=conversation`
    // so a tap opens the thread rather than the router's default (resume) tab.
    // Validate against the known tabs so a junk query can't build a bogus path.
    // Hardcoding `/conversation` here let a slow `selectConversationIndex` race a
    // user's tab click and silently undo the navigation (ONE-5844).
    const tabFromQuery = CANDIDATE_TABS.includes(route.query.tab as CandidateTab)
      ? (route.query.tab as CandidateTab)
      : undefined;
    const currentTab = tabFromQuery || getCurrentCandidateTab(route.path);

    // Clean up the URL.
    await safeJobsNavigate(
      {
        path: `/jobs/${project.id}/candidates/${targetConversationId}/${currentTab}`,
        query: {
          ...route.query,
          conversationId: undefined,
          jobId: undefined,
          tab: undefined,
        },
      },
      'replace',
      signal,
    );
  }

  // =================================================================================
  // Watchers
  // =================================================================================

  // Track previous route to detect tab changes
  let previousProjectId: number | undefined;
  let previousConversationId: number | undefined;
  let previousDeepLinkTarget: string | undefined;

  // Tracked by identity so a superseded run's finally cannot release the newer run's hold on the status watcher.
  let activeRouteRun: AbortController | null = null;
  // Ended by the latest run only: a newer run that has not claimed the list yet still needs the skeleton.
  let releaseJobSwitch: (() => void) | undefined;

  // Keyed to the target, so a redirect that a user navigation cancels cannot swallow that navigation.
  let programmaticNavigationTarget: string | null = null;

  /**
   * Main watcher to handle all route changes on the jobs page.
   * Replaces the old `onMounted` and `watch` logic with a single, consolidated handler.
   */
  watch(
    () => route.fullPath, // React to any URL change.
    async () => {
      // Jobs filter keys stay out: a filter change on a pending landing is not a new target.
      const deepLinkTarget = JSON.stringify([
        queryConversationId.value,
        jobApplicantId.value,
        jobId.value,
      ]);
      // The latch only absorbs echoes of the same target; a quick return to another job
      // must still supersede the run it interrupts.
      if (
        abortState.value.recentlyAborted &&
        isOnManagedRoute() &&
        projectId.value === previousProjectId &&
        conversationId.value === previousConversationId &&
        deepLinkTarget === previousDeepLinkTarget
      ) {
        return;
      }

      if (!isOnManagedRoute()) {
        createNewAbortController();
        return;
      }

      const currentProjectId = projectId.value;
      const currentConversationId = conversationId.value;

      // Check if we're just changing tabs (same project and conversation)
      const isTabChange =
        currentProjectId === previousProjectId &&
        currentConversationId === previousConversationId &&
        currentProjectId &&
        currentConversationId;

      // Check if this is a user clicking a different conversation in the same project
      const isUserClickingConversation =
        currentProjectId === previousProjectId &&
        currentConversationId !== previousConversationId &&
        currentProjectId !== undefined &&
        currentConversationId !== undefined &&
        previousConversationId !== undefined;

      // Update previous values for next comparison
      previousProjectId = currentProjectId;
      previousConversationId = currentConversationId;
      previousDeepLinkTarget = deepLinkTarget;

      // Tabs share the selected candidate's pending profile and eligibility reads.
      // Keep those requests alive because a tab change does not start a replacement.
      if (isTabChange) {
        return;
      }

      const controller = createNewAbortController();
      const signal = controller.signal;

      // Skip if this is a programmatic navigation after loading conversations
      if (
        programmaticNavigationTarget !== null &&
        [route.path, route.redirectedFrom?.path].includes(programmaticNavigationTarget)
      ) {
        programmaticNavigationTarget = null;
        return;
      }
      // After the skip: a click started once the list rendered must outlive the run's own redirect.
      // A click from a URL with no candidate yet is still a pick from the loaded list,
      // but only when that list is the target job's.
      const targetProject = projectService.currentProject;
      const isCardNavigation =
        conversationIndexService.hasPendingCardSelection &&
        !!targetProject &&
        targetProject.id === currentProjectId &&
        conversationIndexService.conversationIndexSearch.jobIds.includes(targetProject.jobId);
      conversationIndexService.cancelCardSelection();

      // Captured before the try so the catch below can fail the pending
      // search jump for this exact landing even if the route moved on.
      const pendingJumpApplicationId = jobApplicantId.value;

      try {
        activeRouteRun = controller;

        // Check if we were aborted before processing
        if (signal.aborted) {
          return;
        }

        // A switch whose list has not landed passes to this run, so the cancelled run's late
        // list cannot end the skeleton while this run is still resolving its target.
        const inheritsPendingSwitch =
          releaseJobSwitch !== undefined &&
          conversationIndexService.conversationIndexes.length === 0;
        // The old job's list and pane must not stay clickable while the new job loads.
        if (
          inheritsPendingSwitch ||
          (currentProjectId !== undefined && projectService.currentProject?.id !== currentProjectId)
        ) {
          releaseJobSwitch = conversationIndexService.beginJobSwitch();
        }

        if (pendingJumpApplicationId) {
          // `landed` is decided inside handleJobApplicantRoute BEFORE its
          // URL-cleaning replace re-triggers (and aborts) this watcher, so
          // reading `signal.aborted` here would misread every successful jump.
          const landed = await handleJobApplicantRoute(
            pendingJumpApplicationId,
            jobId.value,
            signal,
          );
          const confirmed = confirmPendingGlobalSearchJump(pendingJumpApplicationId, !landed);
          if (confirmed) {
            // The pane never remounts on a jump (scrollSelectedConversationIntoView
            // only runs onMounted), so bring the landed candidate into view here.
            await nextTick();
            const conversationId =
              conversationIndexService.selectedConversationIndex?.conversationId;
            document
              .getElementById(`conversation-index-card-${conversationId}`)
              ?.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
          }
        } else if (queryConversationId.value) {
          await handleConversationQueryParam(queryConversationId.value, jobId.value, signal);
        } else if (projectId.value && conversationId.value) {
          // Pass the flag to indicate this is a user click navigation
          await handleConversationRoute(
            projectId.value,
            conversationId.value,
            isUserClickingConversation || isCardNavigation,
            null,
            signal,
          );
        } else if (projectId.value) {
          // When navigating between projects, preserve the user's status filter.
          await handleProjectRoute(projectId.value, true, signal);
        } else {
          // Default case for `/jobs` with no params.
          await handleNoProjectRoute(signal);
        }
      } catch (error) {
        // A superseded run (the user navigated on before this stale request
        // failed — the fetches are never actually cancelled) must not toast,
        // track a failed jump, or report the error on whatever page they are
        // on now. Discard a dead search jump silently and bail out entirely.
        if (signal.aborted) {
          if (pendingJumpApplicationId) discardPendingGlobalSearchJump(pendingJumpApplicationId);
          return;
        }
        // A failed search jump must never dead-end (ONE-6912): the deep-link
        // fallbacks below are tuned for email links — restore the exact
        // pre-jump context instead. Ordinary deep links consume nothing here
        // (failPendingGlobalSearchJump returns null) and keep their fallbacks.
        const failedSearchJump: PendingGlobalSearchJump | null = pendingJumpApplicationId
          ? failPendingGlobalSearchJump(pendingJumpApplicationId, error)
          : null;
        if (failedSearchJump) {
          if (!(error instanceof ProjectNotFoundError)) {
            ErrorService.captureException(error);
          }
          // getProjectByJobIdFromDeepLink flags backend-removed jobs so the
          // projects bar swaps to its deleted-job panel; the restored context
          // replaces that fallback, so clear the flag.
          projectService.isProjectFromDeepLinkRemoved = false;
          SnackbarService.cautionAlt(
            "Couldn't open that candidate — the job may no longer be available.",
          );
          if (!signal.aborted) {
            // Deliberately a plain replace (not safeJobsNavigate) and with the
            // double-processing guard cleared: the watcher must reprocess the
            // restored URL so the previously selected conversation is reloaded.
            abortState.value.recentlyAborted = false;
            await router.replace(failedSearchJump.returnPath || '/jobs');
          }
          return;
        }
        if (error instanceof ProjectNotFoundError) {
          SnackbarService.cautionAlt('This job is no longer available.');
          await recoverToJobsIndex(signal);
          return;
        }
        ErrorService.captureException(error);
        SnackbarService.critical(
          error instanceof Error ? error.message : 'Could not load page data',
        );
        // The mobile pending detail screen is derived from the `?conversationId=`
        // query; clear it so a failed resolution falls back to the Activity list
        // instead of leaving the skeletons up. Desktop keeps the URL as is.
        if (queryConversationId.value && isAutoRedirectDisabled()) {
          await recoverToJobsIndex(signal);
        }
      } finally {
        if (activeRouteRun === controller) {
          activeRouteRun = null;
          releaseJobSwitch?.();
          releaseJobSwitch = undefined;
        }
      }
    },
    { immediate: true },
  );

  /**
   * Watches for changes in the status filter and updates the conversation list.
   */
  watch(
    () => conversationIndexService.selectedStatusList,
    async (newStatus, oldStatus) => {
      // Skip if we're already handling a route change
      if (activeRouteRun) {
        return;
      }
      // Skip if no project is selected or if the status hasn't meaningfully changed.
      if (!projectId.value || JSON.stringify(newStatus) === JSON.stringify(oldStatus)) {
        return;
      }
      // Skip the initial population of the status list.
      if (!oldStatus) {
        return;
      }

      // Skip if we don't have a jobId yet
      const jobId = projectService.currentProject?.jobId;
      if (!jobId) {
        return;
      }

      // Create abort controller for this operation
      const controller = createNewAbortController();
      const signal = controller.signal;
      conversationIndexService.cancelCardSelection();

      try {
        const searchParams = withLiveFilters(conversationIndexService, {
          applicationStatus: newStatus,
          jobIds: [jobId],
        });

        await conversationIndexService.updateConversationIndexSearch(searchParams, {
          resetConversationIndexes: true,
          resetPagination: true,
          signal,
        });

        if (signal.aborted) return;

        // After filtering, if the currently viewed conversation is no longer in the list,
        // navigate to the first conversation in the new list.
        const currentConversationStillValid =
          conversationId.value &&
          conversationIndexService.conversationIndexes.some(
            (conv) => conv.conversationId === conversationId.value,
          );

        // Mobile stays on the Activity inbox: a status change must not auto-redirect
        // into a conversation (mirrors the guards in the route handlers above).
        if (isAutoRedirectDisabled()) {
          return;
        }

        // eslint-disable-next-line max-lines
        if (
          !currentConversationStillValid &&
          conversationIndexService.conversationIndexes.length > 0
        ) {
          // Status filter changed and the previously-selected conversation no
          // longer matches — pick the first conversation but keep the user on
          // their current tab. ONE-5844 originally hardcoded `/conversation`
          // here too.
          const firstConversation = conversationIndexService.conversationIndexes[0];
          const currentTab = getCurrentCandidateTab(route.path);
          await redirectToAutoSelected(
            {
              path: `/jobs/${projectId.value}/candidates/${firstConversation.conversationId}/${currentTab}`,
            },
            signal,
          );
        } else if (
          !conversationId.value &&
          !queryConversationId.value &&
          conversationIndexService.conversationIndexes.length === 0
        ) {
          // The empty-result cleanup pins the stage in this route's URL, and handleProjectRoute
          // prefers it. Keep it current, or a reload restores the stage the user left.
          const stage = newStatus.join(',');
          if ((route.query.applicationStatus ?? '') !== stage) {
            await safeJobsNavigate(
              {
                path: `/jobs/${projectId.value}/candidates`,
                query: { ...route.query, applicationStatus: stage || undefined },
              },
              'replace',
              signal,
            );
          }
        }
      } catch (error) {
        ErrorService.captureException(error);
        SnackbarService.critical('Failed to update status filter');
      }
    },
    { deep: true },
  );

  // A popover filter change refetches and auto-selects the first row without touching the
  // URL. Left stale, a reload or Back would treat it as a deep link to an excluded candidate
  // and widen (and save) the user's origin selection.
  let filterChangePendingResync = false;

  let emptyResultCleanupPending = false;

  watch(
    () => {
      const { onlyCurrentUser, onlyUnread, origins } =
        conversationIndexService.conversationIndexSearch;
      return JSON.stringify([onlyCurrentUser, onlyUnread, origins]);
    },
    () => {
      filterChangePendingResync = true;
      emptyResultCleanupPending = true;
    },
    { flush: 'sync' },
  );

  watch(
    () => conversationIndexService.loadingConversationIndexes,
    async (loading, wasLoading) => {
      if (loading || !wasLoading || !filterChangePendingResync) {
        return;
      }
      filterChangePendingResync = false;

      if (!projectId.value || !conversationId.value || isAutoRedirectDisabled()) {
        return;
      }

      const conversationStillInList = conversationIndexService.conversationIndexes.some(
        (conv) => conv.conversationId === conversationId.value,
      );
      const firstConversation = conversationIndexService.conversationIndexes[0];
      // Only a refetch that auto-selected its first row is a filter change. A route handler's
      // own refetch inserts its deep-linked target afterwards and selects nothing here.
      const autoSelectedFirst =
        !!firstConversation &&
        conversationIndexService.selectedConversationIndex?.conversationId ===
          firstConversation.conversationId;
      if (conversationStillInList || !autoSelectedFirst) {
        return;
      }

      // A replace of the current route's own list state, so it reuses the live controller:
      // a fresh one would abort it and make the route watcher swallow this very redirect.
      await safeJobsNavigate(
        {
          path: `/jobs/${projectId.value}/candidates/${
            firstConversation.conversationId
          }/${getCurrentCandidateTab(route.path)}`,
        },
        'replace',
        abortState.value.controller?.signal,
      );
    },
  );

  // A filter change that settles on a successful, empty list leaves the URL on a candidate the
  // list no longer shows. Reload and Back would then read it as a deep link to an excluded
  // candidate, widen the origins and save that. Drop the candidate and keep everything else,
  // including the stage, which the bare route would otherwise re-default.
  watch(
    () => conversationIndexService.latestListFetchResult,
    async (result) => {
      if (!result || !emptyResultCleanupPending) {
        return;
      }
      if (conversationIndexService.loadingConversationIndexes) {
        return;
      }
      emptyResultCleanupPending = false;

      // A route handler's deep-link load settles empty before it inserts its own target, so
      // only a load that auto-selects (the popover's) can be read as the final list.
      if (!result.succeeded || result.rowCount > 0 || !result.autoSelectedFirst) {
        return;
      }
      if (!projectId.value || !conversationId.value || isAutoRedirectDisabled()) {
        return;
      }
      const signal = abortState.value.controller?.signal;
      if (signal?.aborted) {
        return;
      }

      conversationIndexService.selectedConversationIndex = null;
      conversationIndexService.clearSelectedProAndApplication();

      const stage = conversationIndexService.selectedStatusList.join(',');
      await safeJobsNavigate(
        {
          path: `/jobs/${projectId.value}/candidates`,
          query: { ...route.query, applicationStatus: stage || undefined },
        },
        'replace',
        signal,
      );
    },
  );

  // Cancel operations when leaving the route
  onBeforeRouteLeave((to) => {
    // `/jobs` and `/jobs/:projectId/candidates` are separate records: the landing redirect
    // between them is a leave, and the route watcher already decides which clicks it supersedes.
    if (!to.path.startsWith('/jobs')) conversationIndexService.cancelCardSelection();
    if (abortState.value.controller) {
      abortState.value.controller.abort();
    }
  });

  // Cleanup on unmount
  onBeforeUnmount(() => {
    conversationIndexService.cancelCardSelection();
    if (abortState.value.controller) {
      abortState.value.controller.abort();
      // Clean up the ref to prevent memory leaks
      abortState.value.controller = null;
      abortState.value.recentlyAborted = false;
    }
  });
}
